import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Tag, Rate, Empty, Radio } from "antd";
import { HeartHandshake, Flower2, Sparkles, ArrowRight, Clock, Target } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

import feritwell from '../../assets/ferti_well.jpeg';
import Novaher from '../../assets/nova_her.jpg';
import Novalife from '../../assets/nova_life.jpg';

const ease = [0.22, 1, 0.36, 1];

const services = [
  {
    id: 1,
    icon: HeartHandshake,
    title: "Ferti well",
    desc: "Personalized holistic therapies to support fertility and reproductive wellness.",
    img: feritwell,
    duration: "60 mins",
    goal: "Reproductive Care",
  },
  {
    id: 2,
    icon: Flower2,
    title: "Nova her",
    desc: "Holistic care for hormonal balance, menstrual health, and women's well-being.",
    img: Novaher,
    duration: "60 mins",
    goal: "Hormonal Health",
  },
  {
    id: 3,
    icon: Sparkles,
    title: "Nova life",
    desc: "Personalized holistic therapies to restore balance, vitality, and overall wellness.",
    img: Novalife,
    duration: "45 mins",
    goal: "Body Wellness",
  },
];

const goals = ["All", "Reproductive Care", "Hormonal Health", "Body Wellness"];

export default function Services() {
  const [activeGoal, setActiveGoal] = useState("All");
  const [activeDuration, setActiveDuration] = useState("All");
  const navigate = useNavigate();

  const filteredServices = services.filter((service) => {
    const matchGoal = activeGoal === "All" || service.goal === activeGoal;
    const matchDuration = activeDuration === "All" || service.duration === activeDuration;
    return matchGoal && matchDuration;
  });

  const bookTreatment = (title) => {
    navigate("/appointment", { state: { treatment: title } });
  };

  return (
    <section className="pb-20 bg-[#faf6ee]  overflow-hidden relative">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6"
        >
          <div className="w-full justify-center item-center text-center">
            <h2 className="uppercase md:text-xl text-base font-extrabold text-brand-gold">
              Our Services
            </h2>
            <h3 className="text-4xl lg:text-5xl font-bold text-brand-forest">Women's Wellness & Holistic Care</h3>
          </div>
        
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="flex flex-col lg:flex-row gap-6 mb-12 bg-white/50 p-6 rounded-2xl border border-brand-lightgold"
        >
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-widest text-brand-forest mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" /> Filter by Health Goal
            </div>
            <Radio.Group
              value={activeGoal}
              onChange={(e) => setActiveGoal(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              {goals.map((goal) => (
                <Radio.Button key={goal} value={goal} className="!rounded-full !mr-2 mb-2">
                  {goal}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[400px]">
          <AnimatePresence mode="popLayout">
            {filteredServices.map((service) => {
              const Icon = service.icon;
              return (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, ease }}
                  whileHover={{ y: -10 }}
                  key={service.id}
                >
                  <Card
                    hoverable
                    className="group !rounded-3xl !overflow-hidden !shadow-lg hover:!shadow-2xl !transition-all !duration-500 !border-yellow-100 h-full"
                    styles={{ body: { padding: 0 } }}
                    cover={
                      <div className="relative w-full h-[220px] overflow-hidden">
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/5 transition duration-500 z-10 pointer-events-none" />
                        <img
                          src={service.img}
                          alt={service.title}
                          referrerPolicy="no-referrer"
                          className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition duration-700"
                        />
                        <button
                          onClick={() => bookTreatment(service.title)}
                          className="absolute bottom-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-500 bg-brand-gold text-white px-6 py-3 rounded-full z-30 font-semibold hover:bg-brand-forest cursor-pointer"
                        >
                          Book Now
                        </button>
                        <div className="absolute top-5 left-5 z-20 w-14 h-14 rounded-full bg-white border-2 border-yellow-300 shadow-xl flex items-center justify-center text-brand-gold group-hover:rotate-12 group-hover:scale-110 transition-all duration-500">
                          <Icon className="w-5 h-5" />
                        </div>
                      </div>
                    }
                  >
                    <div className="p-8 flex-1 flex flex-col">
                      <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <Tag className="!bg-brand-beige !border-none !text-brand-forest !text-[10px] !font-bold !uppercase !tracking-widest !rounded-full !px-3 !py-1">
                          {service.goal}
                        </Tag>
                        <Tag className="!bg-brand-beige !border-none !text-gray-500 !text-[10px] !font-bold !uppercase !tracking-widest !rounded-full !px-3 !py-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {service.duration}
                        </Tag>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <Rate disabled defaultValue={5} className="!text-sm" />
                        <span className="text-xs text-gray-500">4.9 (250 Reviews)</span>
                      </div>

                      <h4 className="font-bold text-xl text-brand-forest mb-3 flex items-center justify-between">
                        {service.title}
                        <ArrowRight className="w-5 h-5 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-brand-gold" />
                      </h4>

                      <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1">{service.desc}</p>

                      <div className="w-full h-[1px] bg-brand-lightgold group-hover:bg-brand-gold/30 transition-colors duration-300" />
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredServices.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="col-span-full py-20">
              <Empty description="No treatments found. Try adjusting your filters.">
                <Button
                  onClick={() => {
                    setActiveGoal("All");
                    setActiveDuration("All");
                  }}
                  className="!bg-brand-forest !text-white hover:!bg-brand-gold !border-none"
                >
                  Clear Filters
                </Button>
              </Empty>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
